import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Candidate,
  ClientMessage,
  Member,
  RoomState,
  RoundBasis,
  ServerMessage,
} from './protocol.js';

export type JamStatus = 'connecting' | 'ready' | 'reconnecting' | 'error';

export type Notice = { level: 'info' | 'error'; message: string; at: number };

/** 時刻オフセットの計測回数。中央値を採るので奇数にする */
const SYNC_SAMPLES = 5;
const SYNC_SAMPLE_INTERVAL_MS = 200;
const RESYNC_INTERVAL_MS = 60_000;

export type Jam = {
  status: JamStatus;
  state: RoomState | null;
  me: Member | null;
  notice: Notice | null;
  searchResults: { query: string; results: Candidate[] } | null;
  clearSearchResults: () => void;
  /** サーバ時刻の「今」。再生位置の計算はすべてこれを基準にする */
  serverNow: () => number;
  clockOffsetMs: number;
  send: (msg: ClientMessage) => void;
  actions: {
    add: (input: string) => void;
    search: (query: string) => void;
    addCandidate: (candidate: Candidate) => void;
    remove: (trackId: string) => void;
    skip: () => void;
    pause: () => void;
    resume: () => void;
    seek: (positionMs: number) => void;
    setAudio: (enabled: boolean) => void;
    setRoundBasis: (basis: RoundBasis) => void;
    rename: (name: string) => void;
    claimLeader: () => void;
    reportMeta: (meta: {
      trackId: string;
      durationMs: number;
      title?: string;
      artist?: string;
    }) => void;
    reportEnded: (trackId: string) => void;
    syncPosition: (trackId: string, positionMs: number) => void;
    leave: () => void;
  };
};

export function useJam(roomCode: string, name: string): Jam {
  const [status, setStatus] = useState<JamStatus>('connecting');
  const [state, setState] = useState<RoomState | null>(null);
  const [me, setMe] = useState<Member | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [searchResults, setSearchResults] = useState<Jam['searchResults']>(null);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  // 名前の変更で WebSocket を張り直さないよう ref で持つ。
  // 再接続すると切断扱いになり、猶予期間の処理が走ってしまう。
  const nameRef = useRef(name);
  nameRef.current = name;
  // 再生ループが毎フレーム最新値を読むため、state ではなく ref で持つ
  const offsetRef = useRef(0);
  const samplesRef = useRef<number[]>([]);
  const retryRef = useRef(0);
  const closedByUsRef = useRef(false);

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  /**
   * 時刻合わせ。WebSocket 上で往復時間を測り、片道分を差し引く（NTP と同じ）。
   *   offset = tServer - (t0 + t1) / 2
   * 1回だと外れ値に弱いので複数回測って中央値を採る。
   */
  const runClockSync = useCallback(
    (ws: WebSocket) => {
      samplesRef.current = [];
      for (let i = 0; i < SYNC_SAMPLES; i++) {
        setTimeout(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          ws.send(JSON.stringify({ t: 'ping', t0: Date.now() } satisfies ClientMessage));
        }, i * SYNC_SAMPLE_INTERVAL_MS);
      }
    },
    [],
  );

  useEffect(() => {
    if (!roomCode) return;
    closedByUsRef.current = false;
    let resyncTimer: number | undefined;
    let retryTimer: number | undefined;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${location.host}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        const token = localStorage.getItem(tokenKey(roomCode)) ?? undefined;
        ws.send(
          JSON.stringify({
            t: 'hello',
            roomCode,
            token,
            name: nameRef.current,
          } satisfies ClientMessage),
        );
        runClockSync(ws);
        resyncTimer = window.setInterval(() => runClockSync(ws), RESYNC_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }

        switch (msg.t) {
          case 'welcome':
            localStorage.setItem(tokenKey(roomCode), msg.token);
            setMe(msg.member);
            setState(msg.state);
            setStatus('ready');
            break;

          case 'state':
            setState(msg.state);
            // 自分の情報（音声トグルやホスト権限）も全状態から拾い直す
            setMe((prev) =>
              prev ? msg.state.members.find((m) => m.id === prev.id) ?? prev : prev,
            );
            break;

          case 'pong': {
            const t1 = Date.now();
            const offset = msg.tServer - (msg.t0 + t1) / 2;
            const samples = [...samplesRef.current, offset].slice(-SYNC_SAMPLES);
            samplesRef.current = samples;
            const sorted = [...samples].sort((a, b) => a - b);
            const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
            offsetRef.current = median;
            setClockOffsetMs(Math.round(median));
            break;
          }

          case 'searchResults':
            setSearchResults({ query: msg.query, results: msg.results });
            break;

          case 'notice':
            setNotice({ level: msg.level, message: msg.message, at: Date.now() });
            break;
        }
      };

      ws.onclose = () => {
        if (resyncTimer) window.clearInterval(resyncTimer);
        if (disposed || closedByUsRef.current) return;
        setStatus('reconnecting');
        // 指数バックオフ。上限を置いて復帰が遅くなりすぎないようにする
        const delay = Math.min(1000 * 2 ** retryRef.current, 10_000);
        retryRef.current += 1;
        retryTimer = window.setTimeout(connect, delay);
      };

      ws.onerror = () => {
        if (ws.readyState !== WebSocket.OPEN) setStatus('reconnecting');
      };
    };

    connect();

    return () => {
      disposed = true;
      if (resyncTimer) window.clearInterval(resyncTimer);
      if (retryTimer) window.clearTimeout(retryTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [roomCode, runClockSync]);

  // 通知は数秒で自動的に消す
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const serverNow = useCallback(() => Date.now() + offsetRef.current, []);

  const actions = useMemo<Jam['actions']>(
    () => ({
      add: (input) => send({ t: 'add', input }),
      search: (query) => send({ t: 'search', query }),
      addCandidate: (candidate) => send({ t: 'addCandidate', candidate }),
      remove: (trackId) => send({ t: 'remove', trackId }),
      skip: () => send({ t: 'skip' }),
      pause: () => send({ t: 'pause' }),
      resume: () => send({ t: 'resume' }),
      seek: (positionMs) => send({ t: 'seek', positionMs }),
      setAudio: (enabled) => send({ t: 'setAudio', enabled }),
      setRoundBasis: (basis) => send({ t: 'setRoundBasis', basis }),
      claimLeader: () => send({ t: 'claimLeader' }),
      // token 付きの hello を送り直すと、サーバ側は同一メンバーとして
      // 名前だけを更新する。接続を張り直す必要はない。
      rename: (next) => {
        const token = localStorage.getItem(tokenKey(roomCode)) ?? undefined;
        send({ t: 'hello', roomCode, token, name: next });
      },
      reportMeta: (meta) => send({ t: 'reportMeta', ...meta }),
      reportEnded: (trackId) => send({ t: 'ended', trackId }),
      syncPosition: (trackId, positionMs) =>
        send({ t: 'syncPosition', trackId, positionMs }),
      leave: () => {
        closedByUsRef.current = true;
        send({ t: 'leave' });
        localStorage.removeItem(tokenKey(roomCode));
      },
    }),
    [send, roomCode],
  );

  return {
    status,
    state,
    me,
    notice,
    searchResults,
    clearSearchResults: () => setSearchResults(null),
    serverNow,
    clockOffsetMs,
    send,
    actions,
  };
}

function tokenKey(roomCode: string): string {
  return `jam:token:${roomCode}`;
}
